import { Injectable } from "@angular/core";
import { catchError, Observable, of, ReplaySubject, tap } from "rxjs";
import { environment } from "@env/environment";
import { HttpClient } from "@angular/common/http";
import { Locality, LocalityRequest } from "./locality.interface";

@Injectable()
export class LocalityService {

    baseUrl = environment.administration_url + '/admin/localities';
    private _locality: ReplaySubject<Locality> = new ReplaySubject<Locality>(1);
    private _localities: ReplaySubject<Locality[]> = new ReplaySubject<Locality[]>(1);

    set locality(value: Locality) {
        this._locality.next(value);
    }

    get locality$() {
        return this._locality.asObservable();
    }

    set localities(value: Locality[]) {
        this._localities.next(value);
    }

    get localities$() {
        return this._localities.asObservable();
    }

    constructor(
        private _httpClient: HttpClient
    ) { }

    getAll(): Observable<Locality[]> {
        return this._httpClient.get<Locality[]>(`${this.baseUrl}`)
        .pipe(
            tap((response: Locality[]) => {
                this.localities = response || [];
                return response;
            }),
            catchError(() => of([]))
        );
    }

    getById(uuid: string): Observable<Locality> {
        return this._httpClient.get<Locality>(`${this.baseUrl}/${uuid}`)
        .pipe(
            tap((locality) => {
                this.locality = locality;
                return locality;
            }),
            catchError(() => of({} as Locality))
        );
    }

    getByZoneUuid(zoneUuid: string): Observable<Locality[]> {
        return this._httpClient.get<Locality[]>(`${this.baseUrl}/by-zone/${zoneUuid}`)
        .pipe(
            tap((response: Locality[]) => {
                this.localities = response || [];
                return response;
            }),
            catchError(() => of([]))
        );
    }

    create(data: LocalityRequest): Observable<Locality> {
        return this._httpClient.post<Locality>(`${this.baseUrl}`, data)
        .pipe(
            tap((locality) => {
                this.locality = locality;
                return locality;
            }),
            catchError(() => of({} as Locality))
        );
    }

    update(uuid: string, data: LocalityRequest): Observable<Locality> {
        return this._httpClient.put<Locality>(`${this.baseUrl}/${uuid}`, data)
        .pipe(
            tap((locality) => {
                this.locality = locality;
                return locality;
            }),
            catchError(() => of({} as Locality))
        );
    }

    delete(uuid: string): Observable<void> {
        return this._httpClient.delete<void>(`${this.baseUrl}/${uuid}`)
        .pipe(
            catchError(() => of(undefined))
        );
    }
}
